import { ReactElement, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  AppIcon,
  toolTransform,
  collegeTransform,
  competencyTransform,
  internshipTransform,
  scholarshipTransform,
  careerPathFromMappedTransform,
  majorTransform,
  industryTransform,
  employerTransform,
  coachTransform,
  deepDiveTransform,
  playlistTransform,
} from '@backrs_ui/common-ui';

import {
  getNuggetImageUrl,
  NUGGET_VARIANT_ICON_CONFIG,
} from '@backrs_ui/common-ui/src/molecules/Nugget/utils/nuggetImages.util';
import { handlePanelNuggetClick } from '@backrs_ui/grow-ui/src/components/Nuggets/nugget.util';
import { NuggetChip } from '@backrs_ui/grow-ui/src/components/Nuggets/NuggetChip';
import { useResourceData } from '@backrs_ui/grow-ui/src/hooks/useResourceData';
import { ResourceKey } from '@backrs_ui/grow-ui/src/models/models';

import { Box, SxProps, Theme } from '@mui/material';

type AttachedNuggetChipProps = Readonly<{
  id: string;
  type: ResourceKey;
  onRemove?: () => void;
}>;

export const AttachedNuggetChip = ({ id, type, onRemove }: AttachedNuggetChipProps) => {
  const navigate = useNavigate();
  const resourceData = useResourceData(type, id);

  const contentNugget = useMemo(() => {
    if (resourceData.contentNuggetData) return resourceData.contentNuggetData;
    if (resourceData.toolData) return toolTransform(resourceData.toolData);
    if (resourceData.collegeData) return collegeTransform(resourceData.collegeData);
    if (resourceData.competencyData) return competencyTransform(resourceData.competencyData);
    if (resourceData.internshipData) return internshipTransform(resourceData.internshipData);
    if (resourceData.scholarshipData) return scholarshipTransform(resourceData.scholarshipData);
    if (resourceData.careerPathData)
      return careerPathFromMappedTransform(resourceData.careerPathData);
    if (resourceData.majorData) return majorTransform(resourceData.majorData);
    if (resourceData.industryData) return industryTransform(resourceData.industryData);
    if (resourceData.employerData) return employerTransform(resourceData.employerData);
    if (resourceData.coachData) return coachTransform(resourceData.coachData);
    if (resourceData.deepDiveData) return deepDiveTransform(resourceData.deepDiveData);
    if (resourceData.suggestedPlaybookData)
      return playlistTransform(resourceData.suggestedPlaybookData);

    return null;
  }, [resourceData]);

  const imageResult = contentNugget
    ? getNuggetImageUrl({
        nugget: contentNugget,
      })
    : undefined;

  const renderChipIcon = (): ReactElement | undefined => {
    if (!contentNugget) {
      return undefined;
    }

    const variantConfig = NUGGET_VARIANT_ICON_CONFIG[contentNugget.variant];

    if (variantConfig) {
      return (
        <Box sx={variantIconSx(variantConfig.bgColor)}>
          <AppIcon iconName={variantConfig.icon} diameter="12px" color={variantConfig.iconColor} />
        </Box>
      );
    }

    return undefined;
  };

  if (!contentNugget) {
    return null;
  }

  const chipIcon = renderChipIcon();
  // a tool shows the banner image from its card, not its sticker
  const toolBanner = contentNugget.tool_info?.banner_media?.media_url;
  const fallbackImage = imageResult?.type === 'URL' ? imageResult.url : undefined;
  // a variant with its own icon config keeps it in place of any image
  const imageUrl = chipIcon ? undefined : (toolBanner ?? fallbackImage);

  return (
    <NuggetChip
      id={`attached-nugget-chip-${type}-${id}`}
      label={contentNugget.title}
      image={imageUrl ? { src: imageUrl, alt: contentNugget.title || '' } : undefined}
      icon={chipIcon}
      onClick={() => handlePanelNuggetClick(contentNugget, navigate)}
      onRemove={onRemove}
      sx={labelTruncationSx}
    />
  );
};

const variantIconSx =
  (bgColor: string): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    backgroundColor: backrs.colors[bgColor as keyof typeof backrs.colors],
  });

// a long title truncates rather than widening the chip past its row
const labelTruncationSx: SxProps<Theme> = {
  '& .MuiChip-label': {
    maxWidth: '150px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
};
